export const name = "KLL_QUANTILES.INIT_INT64";
export const summary = `Aggregates values into an \`INT64\`-initialized KLL sketch.`;
export const reference = `
## KLL_QUANTILES.INIT_INT64

Aggregates values into an \`INT64\`-initialized KLL sketch.

\`\`\`
KLL_QUANTILES.INIT_INT64(
  input
  [, precision [, weight => input_weight]]
)
\`\`\`

### Description

Takes one or more \`input\` values and aggregates them into a KLL sketch. This function represents the output sketch using the \`BYTES\` data type. This is an aggregate function.


### Supported Argument Types

- \`input\`: \`INT64\`
- \`precision\`: An \`INT64\` value that defines the exactness of the returned approximate quantile _q_. The default value is 1000. For more information about precision, see Precision for KLL sketches. The value of\`precision\` must be between 1 and 100,000.
- \`input_weight\`: \`INT64\`. By default, values in an initialized KLL sketch are weighted equally as \`1\`. To weight values differently, use the named argument, \`weight\`, which assigns a weight to each input in the resulting KLL sketch. \`weight\` is a multiplier. For example, if you assign a weight of \`3\` to an input value, it's as if three instances of the input value are included in the generation of the KLL sketch. The minimum value for\`weight\` is \`1\` and the maximum value is \`2,147,483,647\`.


### Return Type

KLL sketch as \`BYTES\`


### Examples

The following query takes a column of type \`INT64\` and outputs a sketch as\`BYTES\` that allows you to retrieve values whose ranks are within ±1/1000 \\* 5 = ±1/200 ≈ 0 ranks of their exact quantile.



\`\`\`
SELECT KLL_QUANTILES.INIT_INT64(x, 1000) AS kll_sketch
FROM (SELECT 1 AS x UNION ALL
      SELECT 2 AS x UNION ALL
      SELECT 3 AS x UNION ALL
      SELECT 4 AS x UNION ALL
      SELECT 5 AS x);
\`\`\`

The following examples illustrate how weight works when you initialize a KLL sketch. The results are converted to quantiles.



\`\`\`
WITH points AS (
  SELECT 1 AS x, 1 AS y UNION ALL
  SELECT 2 AS x, 1 AS y UNION ALL
  SELECT 3 AS x, 1 AS y UNION ALL
  SELECT 4 AS x, 1 AS y UNION ALL
  SELECT 5 AS x, 1 AS y)
SELECT KLL_QUANTILES.EXTRACT_INT64(kll_sketch, 2) AS halves
FROM
  (
    SELECT KLL_QUANTILES.INIT_INT64(x, 1000, weight=>y) AS kll_sketch
    FROM points
  );

/*---------*
 | halves |
 +---------+
 | [1,3,5] |
 *---------*/
\`\`\`



\`\`\`
WITH points AS (
  SELECT 1 AS x, 1 AS y UNION ALL
  SELECT 2 AS x, 3 AS y UNION ALL
  SELECT 3 AS x, 1 AS y UNION ALL
  SELECT 4 AS x, 1 AS y UNION ALL
  SELECT 5 AS x, 1 AS y)
SELECT KLL_QUANTILES.EXTRACT_INT64(kll_sketch, 2) AS halves
FROM
  (
    SELECT KLL_QUANTILES.INIT_INT64(x, 1000, weight=>y) AS kll_sketch
    FROM points
  );

/*---------*
 | halves |
 +---------+
 | [1,2,5] |
 *---------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/kll_functions
`;
